import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { SortingState } from "@tanstack/react-table";
import { Search } from "lucide-react";
import usePagination from "@/hooks/useSyncedPagination";
import { useGetAllExpeditionBo } from "@/api/expedition.api";
import { UnifiedInput } from "@/components/ui/customs/unified-input";
import { COUNTRY_REGIONS } from "@/constants/countries";
import { ExpeditionsHistoryTable } from "@/components/backOffice/expeditionsHistory/expeditionsHistoryTable";
import {
  ExpeditionsHistoryFilterTrigger,
  ExpeditionsHistoryFilterPanel,
} from "@/components/backOffice/expeditionsHistory/expeditionsHistoryFilters";
import { useExpeditionFilters } from "@/components/backOffice/pendingExpeditions/useExpeditionFilters.reducer";
import { useFilterV2 } from "@/components/shared/filter-v2";
import { handleExpeditionRowClick } from "@/components/backOffice/shared/expeditionColumns";
import { GetAllExpeditionBoResponseDtoExpedition } from "@/types/api/api-types";

export const ExpeditionsHistoryV2Page = () => {
  const { t } = useTranslation("boExpeditions");
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([{ id: "validatedAt", desc: true }]);
  const [pagination, setPagination] = usePagination("expeditionsHistoryV2");

  const { filters, debouncedSearch, actions } = useExpeditionFilters();
  const { isOpen, toggle } = useFilterV2();

  const dateRangeToParams = (range: [Date, Date] | null, afterKey: string, beforeKey: string) => {
    if (!range) return {};
    return {
      [afterKey]: range[0].toISOString(),
      [beforeKey]: range[1].toISOString(),
    };
  };

  const queryFilter = useMemo(
    () => ({
      includeDeleted: true,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(filters.searchExact ? { searchExact: true } : {}),
      ...(filters.country
        ? { country: filters.country }
        : filters.region
          ? { countries: COUNTRY_REGIONS[filters.region] }
          : {}),
      ...(filters.customId ? { customId: filters.customId } : {}),
      ...(filters.packagingType ? { packagingType: filters.packagingType } : {}),
      ...dateRangeToParams(filters.validatedAt, "validatedAfter", "validatedBefore"),
      ...dateRangeToParams(filters.pickupDate, "pickupDateAfter", "pickupDateBefore"),
      ...dateRangeToParams(
        filters.deliveryFromDate,
        "deliveryFromDateAfter",
        "deliveryFromDateBefore",
      ),
      ...dateRangeToParams(filters.finalizedAt, "finalizedAfter", "finalizedBefore"),
      ...dateRangeToParams(filters.deliveryDate, "deliveryDateAfter", "deliveryDateBefore"),
    }),
    [
      debouncedSearch,
      filters.searchExact,
      filters.country,
      filters.region,
      filters.customId,
      filters.packagingType,
      filters.validatedAt,
      filters.pickupDate,
      filters.deliveryFromDate,
      filters.finalizedAt,
      filters.deliveryDate,
    ],
  );

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [queryFilter, setPagination]);

  const { data: expeditions, isLoading: isExpeditionsLoading } = useGetAllExpeditionBo({
    ...queryFilter,
    take: pagination.pageSize,
    skip: pagination.pageIndex * pagination.pageSize,
    sortBy: sorting[0]?.id,
    sortOrder: sorting[0]?.desc ? "DESC" : "ASC",
  });

  const expeditionsData = useMemo(() => expeditions?.data ?? [], [expeditions]);

  const handleRowClick = useCallback(
    (row: GetAllExpeditionBoResponseDtoExpedition) => handleExpeditionRowClick(row, navigate),
    [navigate],
  );

  return (
    <div className="bg-border/20 flex h-fit min-h-dvh w-full flex-1 flex-col gap-4 p-8">
      <div className="flex w-full flex-col justify-between gap-2 md:flex-row">
        <div className="flex w-fit flex-col gap-2">
          <h1 className="font-jakarta-sans text-3xl font-semibold">{t("historyPage.title")}</h1>
          <p>{t("historyPage.subtitle")}</p>
        </div>
      </div>
      <div className="flex flex-row items-center gap-4">
        <div className="flex w-full flex-col gap-2 sm:flex-row">
          <div className="flex-1">
            <UnifiedInput
              inputType="text"
              leftElement={<Search />}
              value={filters.search}
              onChange={(e) => actions.setSearch(String(e || ""))}
              className="placeholder:text-[#94A3B8]"
              placeholder={t("historyPage.searchPlaceholder")}
            />
          </div>
        </div>
        <ExpeditionsHistoryFilterTrigger
          isOpen={isOpen}
          onToggle={toggle}
          onReset={actions.resetFilters}
        />
      </div>
      <ExpeditionsHistoryFilterPanel isOpen={isOpen} filters={filters} actions={actions} />
      <ExpeditionsHistoryTable
        data={expeditionsData}
        rowCount={expeditions?.count || 0}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        onRowClick={handleRowClick}
        isLoading={isExpeditionsLoading}
      />
    </div>
  );
};
